import { useEffect, useState } from 'react';
import { createPortal } from 'react-dom';

import { useCamada } from '@ds/feedback';
import { sugestoesDoModulo } from './acoes';
import { BrainApp } from './BrainApp';

export interface PainelDoBrainProps {
    aberto: boolean;
    aoFechar: () => void;
    /** O módulo em que a pessoa está: as sugestões vêm dele. */
    moduloAtual?: string;
}

/**
 * A área visível da janela. No celular o teclado virtual encolhe só o `visualViewport`:
 * sem acompanhá-lo, o campo de mensagem ficaria atrás do teclado.
 */
function useAreaVisivel(ativa: boolean): { altura: number; topo: number } | null {
    const [area, setArea] = useState<{ altura: number; topo: number } | null>(null);
    useEffect(() => {
        const vv = typeof window === 'undefined' ? undefined : window.visualViewport;
        if (!ativa || !vv) {
            setArea(null);
            return undefined;
        }
        const medir = () => setArea({ altura: Math.round(vv.height), topo: Math.round(vv.offsetTop) });
        medir();
        vv.addEventListener('resize', medir);
        vv.addEventListener('scroll', medir);
        return () => {
            vv.removeEventListener('resize', medir);
            vv.removeEventListener('scroll', medir);
        };
    }, [ativa]);
    return area;
}

/**
 * A VecchIA aberta pelo orbe da topbar, em qualquer tela da clínica. Desde 13/09/2026
 * (decisão do usuário) ela cobre a janela inteira — sidebar e topbar do VecchIA incluídas
 * — no desktop e no tablet, e no celular vira app de conversa em tela cheia, como o
 * ChatGPT ou o Gemini. É a mesma tela da rota `/app/brain`, com "fechar" e as sugestões do
 * módulo de onde a pessoa veio. Camada pelo `useCamada`: Esc fecha, o foco fica dentro,
 * volta ao orbe ao fechar, e a página de trás não rola.
 */
export function PainelDoBrain({ aberto, aoFechar, moduloAtual }: PainelDoBrainProps) {
    const ref = useCamada<HTMLDivElement>({ aberta: aberto, aoEsc: aoFechar, seletorDoPrimeiroFoco: 'textarea:not([disabled]), button:not([disabled])' });
    const area = useAreaVisivel(aberto);
    if (!aberto) return null;
    return createPortal(
        <div
            ref={ref} className="brain-painel" role="dialog" aria-modal="true" aria-label="VecchIA" tabIndex={-1}
            style={area ? { height: `${String(area.altura)}px`, top: `${String(area.topo)}px` } : undefined}
        >
            <BrainApp sobreposta abrirNaVoz aoFechar={aoFechar} sugestoesDoContexto={sugestoesDoModulo(moduloAtual)} />
        </div>,
        document.body,
    );
}
